/**
 * Schéma de style d'un élément (tableau, carte info, volet, bouton…) : chaque
 * réglage y est déclaré UNE fois, et tout le reste s'en déduit — les props
 * qu'un style pose, les défauts, et pour les éléments qu'un document porte
 * aussi, les conversions entre le format du classique et celui du site.
 * Ajouter ou retirer un réglage, c'est une entrée ici (plus son champ et son
 * rendu), jamais une liste à tenir à côté.
 *
 * Un réglage : `{ default, style, seed, only, classic }`
 * - `default` : sa valeur de départ, ou `(context) => valeur` quand elle
 *   dépend du document (sa police…). Absent : le réglage naît sans valeur.
 * - `style` : qui le pose. `'gallery'` (défaut) : les styles de la galerie du
 *   bloc comme ceux du thème ; `'theme'` : les styles du thème seulement ;
 *   `false` : aucun style — un réglage commun au document et au site que le
 *   passage de l'un à l'autre recopie, sans être du style (contenu, pied de
 *   tableau…).
 * - `seed` : valeur PROPRE AU BLOC. Un style la pose quand le bloc le prend
 *   (dépôt, clic sur une tuile), mais le bloc ne la suit pas ensuite : elle
 *   dépend de l'instance (nombre d'étoiles, alignement dans la colonne…). La
 *   régler ne détache pas le bloc de son style du thème.
 * - `only` : `'site'` ou `'document'` s'il n'existe que d'un côté. Absent :
 *   des deux.
 * - `classic` : `{ read(style, context), write(value, values) }` quand le
 *   classique le range sous un autre nom ou un autre format. `read` reçoit le
 *   défaut résolu dans `context.fallback` ; `write` rend le patch du style
 *   classique et voit toutes les valeurs du site (certains réglages du
 *   classique en combinent plusieurs). L'un ou l'autre absent, ou `classic`
 *   entier : même nom, même valeur des deux côtés.
 */

const GALLERY = ['gallery'];

const styleOf = (param) => (param.style === undefined ? 'gallery' : param.style);

const resolveDefault = (param, context) => (
  typeof param.default === 'function' ? param.default(context) : param.default
);

const entries = (schema, keep) => Object.entries(schema).filter(([, param]) => keep(param));

const onSite = (param) => param.only !== 'document';

/** Les props du site que posent les styles de ces portées. */
export function styleKeys(schema, styles = GALLERY) {
  return entries(schema, (param) => onSite(param) && styles.includes(styleOf(param)))
    .map(([key]) => key);
}

/** Les valeurs propres au bloc (`seed`) parmi ces props. */
export function seedKeys(schema, keys) {
  return keys.filter((key) => schema[key]?.seed);
}

/** Ces props, sans les valeurs propres au bloc : celles qu'un style impose. */
export function withoutSeeds(schema, keys) {
  return keys.filter((key) => !schema[key]?.seed);
}

/** Les valeurs de départ côté site des réglages de ces portées. */
export function styleDefaults(schema, styles = GALLERY, context = {}) {
  return Object.fromEntries(
    entries(schema, (param) => onSite(param) && styles.includes(styleOf(param)) && 'default' in param)
      .map(([key, param]) => [key, resolveDefault(param, context)]),
  );
}

/** Les valeurs de départ des réglages communes au document et au site. */
export function sharedDefaults(schema, context = {}) {
  return Object.fromEntries(
    entries(schema, (param) => !param.only && 'default' in param)
      .map(([key, param]) => [key, resolveDefault(param, context)]),
  );
}

/** Les valeurs de départ côté document : tout ce que le classique connaît. */
export function classicDefaults(schema, context = {}) {
  return Object.fromEntries(
    entries(schema, (param) => param.only !== 'site' && 'default' in param)
      .map(([key, param]) => [key, resolveDefault(param, context)]),
  );
}

const defined = (pairs) => Object.fromEntries(pairs.filter(([, value]) => value !== undefined));

/**
 * Les valeurs du site d'un style au format du classique. Sans style : les
 * défauts de ce que le document connaît (un réglage propre au site n'a pas
 * de valeur à recevoir d'un document).
 */
export function siteValuesFromClassic(schema, style, context = {}) {
  const shared = entries(schema, (param) => param.only !== 'document');
  if (!style) {
    return defined(shared
      .filter(([, param]) => param.only !== 'site')
      .map(([key, param]) => [key, resolveDefault(param, context)]));
  }
  return defined(shared.map(([key, param]) => [
    key,
    param.classic?.read
      ? param.classic.read(style, { ...context, fallback: resolveDefault(param, context) })
      : style[key],
  ]));
}

/**
 * Les valeurs du site d'une box du document : celles de `siteValuesFromClassic`
 * sans les réglages propres au site, qu'un document ne peut pas imposer (seul
 * un style du thème les pose).
 */
export function siteValuesFromDocument(schema, style, context = {}) {
  const values = siteValuesFromClassic(schema, style, context);
  return Object.fromEntries(Object.entries(values).filter(([key]) => schema[key]?.only !== 'site'));
}

/**
 * Le patch d'un style au format du classique pour ces valeurs du site.
 * Seules les valeurs présentes s'écrivent : le reste du style est gardé.
 */
export function classicValuesFromSite(schema, values) {
  const site = values || {};
  return entries(schema, (param) => param.only !== 'document')
    .filter(([key]) => site[key] !== undefined)
    .reduce((patch, [key, param]) => ({
      ...patch,
      ...(param.classic?.write ? param.classic.write(site[key], site) : { [key]: site[key] }),
    }), {});
}
